import { Grid2 as Grid } from "@mui/material"
import { NumberField } from "../../../components/Fields/NumberField"
import { LabelAndText } from "../../../components/Typography/Typography"
import { FlightlineEventReview } from "../../../models/MissionReview"
import { getDistanceInfo } from "../../../utils/MissionReviewUtils"
import { useMissionReviewStore } from "../../../zstore/MissionReviewStore"

interface ReviewCardDistanceNumberFieldProps {
    start_distance_nm: number
    stop_distance_nm: number
    reviewCardIndex: number
}

export const ReviewCardDistanceNumberFields = (props: ReviewCardDistanceNumberFieldProps) => {
    const { start_distance_nm, stop_distance_nm, reviewCardIndex } = props

    const selectedReviews = useMissionReviewStore((state) => state.currentPartialLines) ?? []
    const updatePartialLines = useMissionReviewStore((state) => state.updatePartialLines)

    const isFirstCard = reviewCardIndex === 0
    const isLastCard = reviewCardIndex === selectedReviews.length - 1

    const currentCard = selectedReviews[reviewCardIndex]
    const previousCard = selectedReviews[reviewCardIndex - 1]
    const nextCard = selectedReviews[reviewCardIndex + 1]
    const { minDist, maxDist } = calculateMinMaxDist(currentCard, previousCard, nextCard)

    const handleUpdateStartDistance = (newDistance: number) => {
        /**
         * Requirements:
         * - change the previous review's stop distance to match the new start distance
         * - do not touch or go past the previous review's START number
         * - do not touch or go past the current review's STOP number
         */

        if (!previousCard.distance_info || !currentCard.distance_info) {
            throw new Error("Unable to Update Start Distance")
        }

        const updatedPrevCard: FlightlineEventReview = {
            ...previousCard,
            distance_info: getDistanceInfo(previousCard.distance_info.start_distance_nm, newDistance),
        }
        const updatedCurCard: FlightlineEventReview = {
            ...currentCard,
            distance_info: getDistanceInfo(newDistance, currentCard.distance_info.stop_distance_nm),
        }

        const updatedSelectedReviews = selectedReviews.toSpliced(
            reviewCardIndex - 1,
            2,
            updatedPrevCard,
            updatedCurCard,
        )
        updatePartialLines(updatedSelectedReviews)
    }

    const handleUpdateStopDistance = (newDistance: number) => {
        /**
         * Requirements:
         * - change the next review's start distance to match the new stop distance
         * - do not touch or go past the current review's START number
         * - do not touch or go past the next review's STOP number
         */

        if (!nextCard.distance_info || !currentCard.distance_info) {
            throw new Error("Unable to Update Start Distance")
        }

        const updatedCurCard: FlightlineEventReview = {
            ...currentCard,
            distance_info: getDistanceInfo(currentCard.distance_info.start_distance_nm, newDistance),
        }

        const updatedNextCard: FlightlineEventReview = {
            ...nextCard,
            distance_info: getDistanceInfo(newDistance, nextCard.distance_info.stop_distance_nm),
        }

        const updatedSelectedReviews = selectedReviews.toSpliced(reviewCardIndex, 2, updatedCurCard, updatedNextCard)
        updatePartialLines(updatedSelectedReviews)
    }

    return (
        <Grid container sx={{ pb: 2 }}>
            <Grid size={{ sm: 6 }}>
                <NumberField
                    fullWidth
                    label={"Start Distance"}
                    disabled={isFirstCard}
                    value={start_distance_nm}
                    step={0.01}
                    decimalCount={2}
                    min={minDist}
                    // cannot exceed the current stop distance
                    max={(currentCard?.distance_info?.stop_distance_nm ?? Number.POSITIVE_INFINITY) - 0.01}
                    onChange={(value) => {
                        if (typeof value === "number") {
                            handleUpdateStartDistance(value)
                        }
                    }}
                    endAdornment={"nMi"}
                />
            </Grid>
            <Grid size={{ sm: 6 }}>
                <NumberField
                    fullWidth
                    label={"Stop Distance"}
                    disabled={isLastCard}
                    value={stop_distance_nm}
                    step={0.01}
                    decimalCount={2}
                    // cannot exceed the current start distance
                    min={(currentCard.distance_info?.start_distance_nm ?? 0) + 0.01}
                    max={maxDist}
                    onChange={(value) => {
                        if (typeof value === "number") {
                            handleUpdateStopDistance(value)
                        }
                    }}
                    endAdornment={"nMi"}
                />
            </Grid>
            <Grid size={{ sm: 12 }} sx={{ px: 2, pt: 2 }}>
                <LabelAndText label="Length">{currentCard.distance_info?.length} nMi</LabelAndText>
            </Grid>
        </Grid>
    )
}

const calculateMinMaxDist = (
    curReview: FlightlineEventReview,
    prevReview?: FlightlineEventReview,
    nextReview?: FlightlineEventReview,
) => {
    let minDist = 0.01
    if (!prevReview && curReview.distance_info) {
        minDist = curReview.distance_info.start_distance_nm + 0.01
    } else if (prevReview?.distance_info) {
        minDist = prevReview.distance_info.start_distance_nm + 0.01
    }

    let maxDist = Number.POSITIVE_INFINITY
    if (nextReview?.distance_info) {
        maxDist = nextReview.distance_info.stop_distance_nm - 0.01
    } else if (!nextReview && curReview.distance_info) {
        maxDist = curReview.distance_info.stop_distance_nm - 0.01
    }

    return { minDist: +minDist.toFixed(2), maxDist: +maxDist.toFixed(2) }
}
